import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { FormSecurity, ErrorSanitizer, SecurityConfig } from "@/lib/security";
import { Check, X, Info, Eye, EyeOff } from "lucide-react";

interface AuthFormProps {
  type: "login" | "register";
  onSubmit: (...args: any[]) => void;
  error: string | null;
  isLoading: boolean;
  compact?: boolean; // New prop for compact display
}

// Password validation requirements
const passwordRequirements = [
  { id: 'length', label: 'At least 8 characters', regex: /.{8,}/ },
  { id: 'uppercase', label: 'One uppercase letter', regex: /[A-Z]/ },
  { id: 'lowercase', label: 'One lowercase letter', regex: /[a-z]/ },
  { id: 'number', label: 'One number', regex: /\d/ },
  { id: 'special', label: 'One special character', regex: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>?/`~]/ }
];

export default function AuthForm({ 
  type, 
  onSubmit, 
  error, 
  isLoading,
  compact = false
}: AuthFormProps) {
  // Using state for form fields to properly control inputs
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    email: "",
    school: ""
  });
  
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState<Record<string, boolean>>({});
  const [passwordFocused, setPasswordFocused] = useState(false);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const { username, password, name, email, school } = formData;
    
    try {
      if (type === "login") {
        // SECURITY: Sanitize email input
        const sanitizedEmail = FormSecurity.sanitizeFormData({ email }).email;
        onSubmit(sanitizedEmail, password);
      } else {
        // SECURITY: Validate and sanitize all registration inputs
        const sanitizedData = FormSecurity.sanitizeFormData({
          username,
          name,
          email,
          school
        });
        
        // SECURITY: Validate input lengths
        FormSecurity.validateFormLimits(sanitizedData);
        
        // Email domain validation removed - all email domains are now accepted
        
        onSubmit(sanitizedData.username, password, sanitizedData.name, sanitizedData.email, sanitizedData.school);
      }
    } catch (error) {
      // SECURITY: Sanitize error messages
      const sanitizedError = ErrorSanitizer.sanitizeErrorMessage(error);
      toast({
        title: "Validation Error",
        description: sanitizedError,
        variant: "destructive"
      });
    }
  };
  
  const validatePassword = (password: string) => {
    const validation: Record<string, boolean> = {};
    passwordRequirements.forEach(req => {
      validation[req.id] = req.regex.test(password);
    });
    setPasswordValidation(validation);
  };

  const handleChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    // SECURITY: Apply basic length limits as user types
    const value = e.target.value;
    const maxLength = SecurityConfig.MAX_LENGTHS[field as keyof typeof SecurityConfig.MAX_LENGTHS] || SecurityConfig.MAX_LENGTHS.general;
    
    const finalValue = value.length <= maxLength ? value : value.substring(0, maxLength);
    
    setFormData(prev => ({
      ...prev,
      [field]: finalValue
    }));
    
    // Validate password in real-time for registration
    if (field === 'password' && type === 'register') {
      validatePassword(finalValue);
    }
  };
  
  const formContent = (
    <form onSubmit={handleSubmit}>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="space-y-3">
        {type === "register" && (
          <>
            <div className={compact ? "mb-2" : ""}>
              <Label htmlFor="name" className={compact ? "text-sm" : ""}>Full Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={handleChange("name")}
                required
                className={compact ? "h-9 mt-1" : "mt-1"}
              />
            </div>
            
            <div className={compact ? "mb-2" : ""}>
              <Label htmlFor="email" className={compact ? "text-sm" : ""}>Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleChange("email")}
                placeholder="your.email@example.com"
                required
                className={compact ? "h-9 mt-1" : "mt-1"}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use any valid email address to create your account
              </p>
            </div>
            
            <div className={compact ? "mb-2" : ""}>
              <Label htmlFor="school" className={compact ? "text-sm" : ""}>School (Optional)</Label>
              <Input
                id="school"
                type="text"
                value={formData.school}
                onChange={handleChange("school")}
                placeholder="University or college name"
                className={compact ? "h-9 mt-1" : "mt-1"}
              />
            </div>
            
            <div className={compact ? "mb-2" : ""}>
              <Label htmlFor="username" className={compact ? "text-sm" : ""}>Username</Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={handleChange("username")}
                required
                className={compact ? "h-9 mt-1" : "mt-1"}
              />
            </div>
          </>
        )}
        
        {type === "login" && (
          <div className={compact ? "mb-2" : ""}>
            <Label htmlFor="email" className={compact ? "text-sm" : ""}>Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
              required
              className={compact ? "h-9 mt-1" : "mt-1"}
            />
          </div>
        )}
        
        <div className={compact ? "mb-3" : ""}>
          <Label htmlFor="password" className={compact ? "text-sm" : ""}>Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange("password")}
              onFocus={() => type === 'register' && setPasswordFocused(true)}
              onBlur={() => setTimeout(() => setPasswordFocused(false), 200)}
              required
              className={`${compact ? "h-9 mt-1" : "mt-1"} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          
          {type === 'register' && passwordFocused && (
            <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <Info className="h-4 w-4" />
                <span className="font-medium">Password requirements:</span>
              </div>
              <ul className="space-y-1">
                {passwordRequirements.map(req => (
                  <li key={req.id} className="flex items-center gap-2 text-sm">
                    {passwordValidation[req.id] ? (
                      <Check className="h-3 w-3 text-green-600" />
                    ) : (
                      <X className="h-3 w-3 text-gray-400" />
                    )}
                    <span className={passwordValidation[req.id] ? "text-green-600" : "text-gray-600"}>
                      {req.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        <Button 
          type="submit" 
          className="w-full" 
          disabled={isLoading}
          size={compact ? "sm" : "default"}
        >
          {isLoading 
            ? type === "login" ? "Signing in..." : "Creating account..." 
            : type === "login" ? "Sign in" : "Create account"
          }
        </Button>
      </div>
    </form>
  );
  
  // If compact, return just the form content, otherwise wrap in Card
  if (compact) {
    return formContent;
  }
  
  return (
    <Card>
      <CardContent className="pt-6">
        {formContent}
      </CardContent>
    </Card>
  );
}
